import { ProjectData } from '../types/projectData';
import { createClientId } from './randomId';

export interface AppState {
  projectData: ProjectData;
  expandedNodeIds: string[];
  menuOrder?: string[];
  activeProjectId?: string | null;
  settings?: {
    darkMode?: boolean;
    autoSync?: boolean;
    syncInterval?: number;
    appleId?: string;
    calendarId?: string;
    isCompactView?: boolean;
  };
  hasUserPreferences?: boolean; // Track if user has set expand/collapse preferences
  collapsedProjects?: string[]; // Track collapsed project IDs for multi-project view
  selectedProjectIds?: string[]; // Track which projects are selected/visible
  
  // View State Preferences
  viewPreferences?: {
    calendar?: {
      view: 'day' | 'week' | 'month' | 'year';
      selectedDate?: string;
    };
    gantt?: {
      zoomLevel?: number;
      scrollPosition?: { x: number; y: number };
      showDependencies?: boolean;
      showCriticalPath?: boolean;
      rowHeight?: number;
    };
    ganttAgentStatus?: {
      readTerminalKeys?: string[];
      readTerminalReadAtByKey?: Record<string, string>;
    };
    dashboard?: {
      layout?: Record<string, unknown>[]; // Widget layout configuration
      widgetStates?: Record<string, unknown>; // Individual widget states
    };
    kanban?: {
      collapsedColumns?: string[];
      swimlaneView?: boolean;
    };
    sidebarCollapsed?: boolean;
  };
  
  // Filter States Per View
  filterStates?: {
    tasks?: {
      search?: string;
      status?: string[];
      assignees?: string[];
      priorities?: string[];
      tags?: string[];
      dateRange?: { start: string; end: string };
    };
    kanban?: {
      assignees?: string[];
      priorities?: string[];
    };
    gantt?: {
      showCompleted?: boolean;
      projectIds?: string[];
    };
  };
  
  // Table Preferences
  tablePreferences?: {
    tasks?: {
      sortBy?: string;
      sortDirection?: 'asc' | 'desc';
      columnWidths?: Record<string, number>;
      visibleColumns?: string[];
    };
    reports?: {
      sortBy?: string;
      sortDirection?: 'asc' | 'desc';
    };
  };
}

const STORAGE_KEY = 'unified_project_state';
const LEGACY_KEYS = ['projectData', 'expandedNodeIds', 'menuOrder', 'darkMode', 'appleId', 'appSpecificPassword', 'calendarId', 'autoSync', 'syncInterval'];
const PROJECT_METADATA_PERSONAL_SETTING_KEYS = new Set([
  'appleId',
  'autoSync',
  'calendarId',
  'darkMode',
  'isCompactView',
  'menuOrder',
  'syncInterval',
]);

let storageErrorCallback: ((error: Error) => void) | null = null;
export const setStorageErrorCallback = (cb: (error: Error) => void) => storageErrorCallback = cb;

const handleError = (op: string, err: unknown) => {
  const message = err instanceof Error ? err.message : String(err);
  storageErrorCallback?.(new Error(`Storage ${op} failed: ${message}`));
};

const getItem = (key: string) => { try { return localStorage.getItem(key); } catch { return null; } };
const parseItem = (key: string) => { const val = getItem(key); return val ? JSON.parse(val) : null; };

const sanitizeProjectDataForLocalCache = (projectData: ProjectData): ProjectData => {
  const metadata = projectData.metadata || {};
  const metadataSettings = metadata.settings || {};
  const sharedSettings = Object.fromEntries(
    Object.entries(metadataSettings).filter(([key]) => !PROJECT_METADATA_PERSONAL_SETTING_KEYS.has(key)),
  );

  return {
    ...projectData,
    metadata: {
      ...metadata,
      settings: sharedSettings as ProjectData['metadata']['settings'],
    },
  };
};

export const storage = {
  load(): AppState | null {
    try {
      const unified = parseItem(STORAGE_KEY);
      if (unified) return unified;

      const projectData = parseItem('projectData');
      if (!projectData) return null;

      const state: AppState = {
        projectData,
        expandedNodeIds: parseItem('expandedNodeIds') || [],
        menuOrder: parseItem('menuOrder'),
        settings: {
          darkMode: parseItem('darkMode'),
          appleId: getItem('appleId') || undefined,
          calendarId: getItem('calendarId') || undefined,
          autoSync: parseItem('autoSync'),
          syncInterval: parseInt(getItem('syncInterval') || '0') || undefined
        }
      };

      if (this.save(state)) this.cleanupLegacy();
      return state;
    } catch (e) {
      handleError('load', e);
      return null;
    }
  },

  save(state: AppState): boolean {
    try {
      const stateForStorage = {
        ...state,
        projectData: state.projectData ? sanitizeProjectDataForLocalCache(state.projectData) : state.projectData,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateForStorage));
      return true;
    } catch (e: unknown) {
      handleError('save', e);
      // A failed complete write must retain the previous cache, all nodes,
      // and unrelated storage keys. Never replace it with a partial graph.
      return false;
    }
  },

  update(updates: Partial<AppState>) {
    this.save({ ...this.load() || {
      projectData: {
        version: '1.0',
        projectId: createClientId('local-project'),
        projectName: 'My Project',
        nodes: [],
        rootNodeIds: [],
        kanbanColumns: [],
        files: [],
        fileAuditLogs: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        metadata: {
          settings: { darkMode: false, autoSync: true, syncInterval: 30000, isCompactView: false, menuOrder: [] }
        }
      },
      expandedNodeIds: [],
      settings: {}
    }, ...updates });
  },

  cleanupLegacy: () => LEGACY_KEYS.forEach(k => { try { localStorage.removeItem(k); } catch { /* best-effort cleanup */ } }),
  // Sign-out drops the cached project graph (titles, descriptions, files) so the next person on a
  // shared browser cannot read it; UI preferences such as dark mode and view settings are kept.
  clearProjectCache() {
    try {
      const unified = parseItem(STORAGE_KEY);
      if (unified && typeof unified === 'object' && 'projectData' in unified) {
        const rest = { ...unified };
        delete rest.projectData;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
      }
      localStorage.removeItem('projectData');
    } catch (e) {
      handleError('clearProjectCache', e);
    }
  },
  clear() { localStorage.removeItem(STORAGE_KEY); this.cleanupLegacy(); },
  forceReload() { this.clear(); sessionStorage.clear(); }
}; 
